import { $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  CLEAR_HISTORY_COMMAND,
  COMMAND_PRIORITY_HIGH,
  KEY_DOWN_COMMAND,
  LexicalEditor,
} from 'lexical';
import { useEffect } from 'react';
import { EMPTY_EDITOR_STATE } from '../../rich-text-editor';

export type TSetupPluginProps = {
  disabled?: boolean;
  maxLength?: number;
  defaultEditorState?: string;
  defaultHtmlState?: string;
  onSetup?: (editor: LexicalEditor) => void;
};

export const SetupPlugin = ({
  defaultEditorState,
  defaultHtmlState,
  maxLength,
  onSetup,
}: TSetupPluginProps) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor) {
      return;
    }

    if (defaultEditorState) {
      const editorState = editor.parseEditorState(
        defaultEditorState || EMPTY_EDITOR_STATE,
      );

      editor.setEditorState(editorState);
    } else if (defaultHtmlState) {
      // Check if the HTML is effectively empty (contains only empty paragraph or whitespace)
      const isEmptyHtml = (html: string): boolean => {
        const trimmedHtml = html.trim();
        return (
          !trimmedHtml ||
          trimmedHtml === '<p><br></p>' ||
          trimmedHtml === '<p></p>' ||
          trimmedHtml === '<br>'
        );
      };

      // If the HTML is empty, don't set it in the editor
      if (isEmptyHtml(defaultHtmlState)) {
        return;
      }

      const wrapHtmlIfNeeded = (html: string): string => {
        const trimmedHtml = html.trim();
        if (!trimmedHtml.startsWith('<') || !trimmedHtml.endsWith('>')) {
          return `<p>${trimmedHtml}</p>`;
        }
        return trimmedHtml;
      };

      editor.update(() => {
        const parser = new DOMParser();
        const wrappedHtml = wrapHtmlIfNeeded(defaultHtmlState);
        const dom = parser.parseFromString(wrappedHtml, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);

        const root = $getRoot();
        root.clear();

        const selection = $getRoot().select();
        selection.insertNodes(nodes);

        editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, defaultEditorState]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    if (!onSetup) return;

    onSetup(editor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.registerCommand(
      KEY_DOWN_COMMAND,
      (e) => {
        const textContentSize =
          (e?.target as HTMLDivElement)?.textContent?.length ?? 0;

        // stop user from typing if maxLength is reached
        if (maxLength && textContentSize >= maxLength) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, maxLength]);

  return null;
};
